import type { Dayjs } from 'dayjs';

import { z as zod } from 'zod';

import { schemaHelper } from 'src/components/hook-form';

import { LinkTypeEnum } from 'src/types/media';

// =============================================
// Enums
// =============================================

export enum PaymentMethodEnum {
  PayPal = 'Paypal',
  WiseTransfer = 'Transfer',
  Pending = 'Pending',
}

// =============================================
// OrderItem (shared between form and billing)
// =============================================

export interface OrderItem {
  mediaName: string;
  price: number;
  currency: number;
  finalUrl: string;
  wordCount: number;
  costPrice?: number;
  costPriceCurrency?: number;
  redactionPrice?: number;
  categoryCommission?: number;
  fileBase64?: string | null;
  imageBase64?: string | null;
  imageTwoBase64?: string | null;
  mediaId?: number | null;
  categoryId?: number | null;
  limitDate?: Date;
  requireRedaction?: boolean;
  url1?: string;
  keyword1?: string;
  linkType1?: number;
  url2?: string;
  keyword2?: string;
  linkType2?: number;
  url3?: string;
  keyword3?: string;
  linkType3?: number;
  url4?: string;
  keyword4?: string;
  linkType4?: number;
  orderDetail?: string;
  title?: string;
  extraTermsToAvoid?: string;
  extraHtml?: string;
  extraSource?: string;
  internalPlatformId?: string;
  directProfit?: number;
  redactionUrl?: string;
  discountPercentage?: number;
}

// =============================================
// Zod Schemas
// =============================================

const dayjsSchema = zod.custom<Dayjs>((val) => {
  if (!val) return 'Se requiere una fecha';
  return true;
});

const basicFields = {
  mediaId: schemaHelper.nullableInput(zod.custom<{ value: number; label: string }>(), {
    message: 'Escoge un medio',
  }),
  price: zod.number().min(1, { message: 'El precio debe ser un numero positivo!' }),
  currency: zod.number().min(0, { message: 'La divisa es obligatoria!' }),
  limitDate: dayjsSchema,
  categoryId: schemaHelper.nullableInput(zod.number(), {
    message: 'Categoria es requerida',
  }),
  categoryCommission: zod.number().default(0),
  requireRedaction: zod.boolean(),
  wordCount: zod.number().default(600),
  orderDetail: zod.string().optional(),
  extraRedaction: zod.boolean().optional(),
  title: zod.string().optional(),
  extraTermsToAvoid: zod.string().optional(),
  extraHtml: zod.string().optional(),
  extraSource: zod.string().optional(),
  image: zod.any().optional(),
  imageTwo: zod.any().optional(),
  costPrice: zod.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    zod.number().optional()
  ),
  costPriceCurrency: zod.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    zod.number().optional()
  ),
  discountPercentage: zod.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    zod.number().optional()
  ),
  directProfit: zod.number().optional(),
  internalPlatformId: zod.string().optional(),
  redactionUrl: zod.string().optional(),
  kabooztCostPrice: zod.number().optional(),
  kabooztDirectProfit: zod.number().optional(),
};

const linkFields = {
  url1: zod.union([zod.string().url('URL no valida'), zod.literal('')]).optional(),
  keyword1: zod.string().optional(),
  // .nullish() (no solo .optional()) porque un pedido ya existente que nunca uso el enlace 2/3/4
  // trae ese campo como null desde el backend, no undefined; con solo .optional() Zod lo
  // rechazaba con "Expected 0 | 1 | 2, received null" en cuanto se validaba el formulario de
  // edicion, aunque el usuario nunca hubiera tocado esos campos.
  linkType1: zod.nativeEnum(LinkTypeEnum).nullish(),
  url2: zod.string().optional(),
  keyword2: zod.string().optional(),
  linkType2: zod.nativeEnum(LinkTypeEnum).nullish(),
  url3: zod.string().optional(),
  keyword3: zod.string().optional(),
  linkType3: zod.nativeEnum(LinkTypeEnum).nullish(),
  url4: zod.string().optional(),
  keyword4: zod.string().optional(),
  linkType4: zod.nativeEnum(LinkTypeEnum).nullish(),
  file: zod.any().optional(),
};

const RedactionSchema = zod.object({
  ...basicFields,
  ...linkFields,
  finalUrl: zod.string().optional(),
  requireRedaction: zod.literal(true),
  // El enlace 1 se muestra con asterisco (obligatorio) en el formulario solo cuando
  // requireRedaction es true; el schema debe exigirlo aqui tambien, si no el * es enganoso
  // y "Anadir al carrito" deja pasar el item sin URL/palabra clave/tipo de enlace.
  url1: zod.string().min(1, { message: 'La URL 1 es obligatoria' }).url('URL no valida'),
  keyword1: zod.string().min(1, { message: 'La palabra clave 1 es obligatoria' }),
  linkType1: zod.nativeEnum(LinkTypeEnum, {
    errorMap: () => ({ message: 'El tipo de enlace 1 es obligatorio' }),
  }),
});

const NoRedactionSchema = zod.object({
  ...basicFields,
  ...linkFields,
  finalUrl: zod.string().optional(),
  requireRedaction: zod.literal(false),
});

export const NewOrderSchema = zod.discriminatedUnion('requireRedaction', [
  RedactionSchema,
  NoRedactionSchema,
]);

export type NewOrderSchemaType = zod.infer<typeof NewOrderSchema>;

export const BillingSchema = zod.object({
  customerId: zod.number().optional(),
  firstName: zod.string().min(1, { message: 'El nombre es obligatorio!' }),
  lastName: zod.string().min(1, { message: 'Los apellidos son obligatorios!' }),
  company: zod.string().optional(),
  email: zod
    .string()
    .email({ message: 'Email invalido!' })
    .min(1, { message: 'El email es obligatorio!' }),
  phone: zod.string().optional(),
  nif: zod.string().min(1, { message: 'La identificacion fiscal es obligatoria!' }),
  country: zod.string().min(1, { message: 'El pais es obligatorio!' }),
  city: zod.string().min(1, { message: 'La ciudad es obligatoria!' }),
  postalCode: zod.string().min(1, { message: 'El codigo postal es obligatorio!' }),
  address: zod.string().min(1, { message: 'La direccion es obligatoria!' }),
  platformId: zod.any().optional(),
});

export const PlatformBillingSchema = zod.object({
  platformId: schemaHelper.nullableInput(zod.custom<{ value: number; label: string }>(), {
    message: 'Escoge una plataforma',
  }),
});

export type BillingSchemaType = zod.infer<typeof BillingSchema>;
export type PlatformBillingSchemaType = zod.infer<typeof PlatformBillingSchema>;
export type CombinedBillingType = BillingSchemaType | PlatformBillingSchemaType;
